import { useEffect, useMemo, useState } from "react";
import { Customer } from "../models/User";

export type CheckoutContinuation = {
  kind: "pending" | "recovery";
  sessionId: string;
  priceId?: string;
  itemName?: string;
  interval?: string;
  mode: "payment" | "subscription";
  expiresAt?: unknown;
  recoveryUrl?: string;
  recoveryUrlExpiresAt?: unknown;
};

export function getTimestampMillis(value: unknown): number | undefined {
  if (value == null) return undefined;
  if (typeof value === "number") return Number.isFinite(value) ? value : undefined;
  if (value instanceof Date) {
    const millis = value.getTime();
    return Number.isNaN(millis) ? undefined : millis;
  }
  if (typeof value === "string") {
    // ISO string or numeric string (server-serialized dates).
    const numeric = Number(value);
    if (value.trim() !== "" && Number.isFinite(numeric)) return numeric;
    const parsed = Date.parse(value);
    return Number.isNaN(parsed) ? undefined : parsed;
  }
  if (typeof value !== "object") return undefined;

  const maybeTimestamp = value as {
    seconds?: number;
    // firebase-admin Timestamps arrive as {_seconds,_nanoseconds} once they
    // pass through res.json() on the workspace session endpoint.
    _seconds?: number;
    toMillis?: () => number;
  };

  if (typeof maybeTimestamp.toMillis === "function") {
    return maybeTimestamp.toMillis();
  }
  if (typeof maybeTimestamp.seconds === "number") {
    return maybeTimestamp.seconds * 1000;
  }
  if (typeof maybeTimestamp._seconds === "number") {
    return maybeTimestamp._seconds * 1000;
  }

  return undefined;
}

// Fail closed: a continuation whose expiry we cannot read must not be offered,
// otherwise the sidebar links to a Stripe session that may already be dead
// ("You're all done here").
function isUnexpired(expiresAt: unknown, now: number): boolean {
  const expiresAtMillis = getTimestampMillis(expiresAt);
  return expiresAtMillis !== undefined && expiresAtMillis > now;
}

// Stripe recovery links stay valid for 30 days after the session expires.
const RECOVERY_URL_LIFETIME_MS = 30 * 24 * 60 * 60 * 1000;

function getRecoveryExpiresAt(
  recovery:
    | { recoveryUrlExpiresAt?: unknown; expiredAt?: unknown }
    | undefined,
): number | undefined {
  const explicit = getTimestampMillis(recovery?.recoveryUrlExpiresAt);
  if (explicit !== undefined) return explicit;
  const expiredAt = getTimestampMillis(recovery?.expiredAt);
  return expiredAt === undefined
    ? undefined
    : expiredAt + RECOVERY_URL_LIFETIME_MS;
}

export function isCheckoutContinuationUsable(
  continuation: CheckoutContinuation,
  now = Date.now(),
): boolean {
  if (continuation.kind === "recovery") {
    return (
      !!continuation.recoveryUrl &&
      isUnexpired(getRecoveryExpiresAt(continuation), now)
    );
  }

  return isUnexpired(continuation.expiresAt, now);
}

function getNextExpiration(
  customerData: Customer | null | undefined,
  now: number,
): number | undefined {
  return [
    getTimestampMillis(customerData?.pendingCheckout?.expiresAt),
    getRecoveryExpiresAt(customerData?.checkoutRecovery),
  ]
    .filter((expiresAt): expiresAt is number => !!expiresAt && expiresAt > now)
    .sort((a, b) => a - b)[0];
}

export function getActiveCheckoutContinuation(
  customerData: Customer | null | undefined,
  now = Date.now(),
): CheckoutContinuation | undefined {
  const pendingCheckout = customerData?.pendingCheckout;
  if (
    pendingCheckout?.sessionId &&
    isUnexpired(pendingCheckout.expiresAt, now)
  ) {
    return {
      kind: "pending",
      sessionId: pendingCheckout.sessionId,
      priceId: pendingCheckout.priceId,
      itemName: pendingCheckout.itemName,
      interval: pendingCheckout.interval,
      mode: pendingCheckout.mode,
      expiresAt: pendingCheckout.expiresAt,
    };
  }

  const checkoutRecovery = customerData?.checkoutRecovery;
  if (
    checkoutRecovery?.recoveryUrl &&
    isUnexpired(getRecoveryExpiresAt(checkoutRecovery), now)
  ) {
    return {
      kind: "recovery",
      sessionId: checkoutRecovery.sessionId,
      priceId: checkoutRecovery.priceId,
      itemName: checkoutRecovery.itemName,
      interval: checkoutRecovery.interval,
      mode: checkoutRecovery.mode,
      recoveryUrl: checkoutRecovery.recoveryUrl,
      recoveryUrlExpiresAt: getRecoveryExpiresAt(checkoutRecovery),
    };
  }

  return undefined;
}

export function useCheckoutContinuation(
  customerData: Customer | null | undefined,
): CheckoutContinuation | undefined {
  const [now, setNow] = useState(() => Date.now());

  useEffect(() => {
    const currentTime = Date.now();
    const nextExpiration = getNextExpiration(customerData, currentTime);
    if (!nextExpiration) return;

    const timeoutMs = Math.min(
      Math.max(nextExpiration - currentTime + 1000, 0),
      2147483647,
    );
    const timeoutId = window.setTimeout(() => setNow(Date.now()), timeoutMs);
    return () => window.clearTimeout(timeoutId);
  }, [customerData, now]);

  return useMemo(
    () => getActiveCheckoutContinuation(customerData, now),
    [customerData, now],
  );
}
